import { useEffect, useRef, useState } from "react";
import { materialProposalBasis, materialsApproved, proposedMaterialsTotal, purchaseMaterialScopeConflict, recipeProcurementSafe, scopeNeedsNoPurchaseMaterials, validMaterialReview, type MaterialAllowanceApproval } from "@workspace/api-zod/material-allowance";
import { type MaterialsLine } from "@workspace/api-zod/materials-list";
import { automaticMaterials, materialFailureMessage } from "../lib/automatic-materials";

let manualLookupSequence = 0;

export function useAutomaticMaterials(items: MaterialsLine[], onChange: ((index: number, value: MaterialAllowanceApproval | null) => void) | undefined,
  enabled: boolean, sessionKey?: string) {
  const latest = useRef({ items, onChange, sessionKey });
  latest.current = { items, onChange, sessionKey };
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const manual = useRef(new Set<string>());
  const attempted = useRef(new Set<string>());
  const requested = useRef(new Map<string, number>());
  const [states, setStates] = useState<Record<string, string>>({});
  const [retryCount, setRetryCount] = useState(0);
  const signature = JSON.stringify(items.map(line => [materialProposalBasis(line), line.materialAllowanceApproval]));
  useEffect(() => {
    if ((!enabled && !requested.current.size) || !sessionKey || !onChange) return;
    items.forEach(line => {
      if (scopeNeedsNoPurchaseMaterials(line)) return;
      const basis = materialProposalBasis(line);
      const explicit = requested.current.get(basis);
      if (!enabled && !explicit) return;
      // Library recipes already provide a purchasing breakdown. Do not create
      // approval drafts or re-identify their materials.
      if (!explicit && line.quoteCostRecipe && recipeProcurementSafe(line)) return;
      const existing = validMaterialReview(line);
      if (!explicit && existing && materialsApproved(line, true)) return;
      // Repair only demonstrably wrong automatic library allocations in an
      // editable draft. Read-only saved views never enter this effect.
      const repairLibrary = existing?.source === "library" && !existing.manualApproved && !existing.requiresApproval &&
        purchaseMaterialScopeConflict(line, existing.materials ?? []);
      const libraryOnly = !explicit && !!existing && !existing.requiresApproval && existing.source === "estimated" && existing.approved !== true
        && !existing.libraryChecked;
      if (!explicit && existing && !libraryOnly && !repairLibrary) {
        setStates(old => old[basis] ? { ...old, [basis]: "" } : old);
        if (existing.mode === "automatic" && existing.approved === undefined)
          latest.current.onChange?.(items.indexOf(line), { ...existing, approved: existing.requiresApproval ? false : existing.source === "library" });
        return;
      }
      const key = `${sessionKey}:${explicit ? `lookup-${explicit}` : repairLibrary ? "repair-library" : libraryOnly ? "recheck" : "identify"}:${basis}:${line.costLibraryTaskId ?? ""}`;
      if (manual.current.has(basis) || attempted.current.has(key)) return;
      attempted.current.add(key);
      const original = JSON.stringify(line.materialAllowanceApproval ?? null);
      const sameTask = (current: MaterialsLine) => (current.costLibraryTaskId ?? null) === (line.costLibraryTaskId ?? null);
      setStates(old => ({ ...old, [basis]: libraryOnly ? "Checking Cost Library…" : "Identifying materials…" }));
      const stillRelevant = () => alive.current && latest.current.sessionKey === sessionKey && !manual.current.has(basis)
        && (!explicit || requested.current.get(basis) === explicit)
        && latest.current.items.some(current => sameTask(current) && materialProposalBasis(current) === basis && (explicit || !current.quoteCostRecipe || !recipeProcurementSafe(current))
          && JSON.stringify(current.materialAllowanceApproval ?? null) === original);
      automaticMaterials(key, line, stillRelevant, libraryOnly).then(proposal => {
        if (!stillRelevant()) return;
        if (explicit && !proposal.materials.length) {
          requested.current.delete(basis);
          setStates(old => ({ ...old, [basis]: "No material suggestion found. You can add materials manually." }));
          return;
        }
        latest.current.items.forEach((current, index) => {
          if (!sameTask(current) || materialProposalBasis(current) !== basis || (!explicit && current.quoteCostRecipe && recipeProcurementSafe(current))
            || JSON.stringify(current.materialAllowanceApproval ?? null) !== original) return;
          if (libraryOnly && proposal.source !== "library") {
            latest.current.onChange?.(index, { ...existing!, libraryChecked: true });
            return;
          }
          const { source, ...materials } = proposal;
          latest.current.onChange?.(index, { version: 1, mode: "automatic",
            source: source === "library" ? "library" : "estimated",
            libraryChecked: true, approved: explicit ? false : source === "library",
            ...(explicit ? { requiresApproval: true } : {}), basis,
            amount: proposedMaterialsTotal(materials.materials), ...materials });
        });
        if (explicit) requested.current.delete(basis);
        setStates(old => ({ ...old, [basis]: "" }));
      }).catch(error => {
        if (stillRelevant()) setStates(old => ({ ...old, [basis]: materialFailureMessage(error) }));
      });
    });
  }, [signature, enabled, sessionKey, retryCount]);
  return { states, startManual: (basis: string) => { manual.current.add(basis); setStates(old => ({ ...old, [basis]: "" })); },
    retry: (basis: string) => {
      manual.current.delete(basis);
      requested.current.set(basis, ++manualLookupSequence);
      setRetryCount(count => count + 1);
    } };
}
